import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["enrollmentType", "sourceApp", "location", "children", "viewTrackingAction", "clickTrackingAction", "shouldReloadOnEnrollmentComplete", "openInCommerceHome", "onClick", "onPaymentsEnrollmentSuccess", "isInProcessorPickerIframe", "tooltipMaxWidth", "tooltipPlacement", "tooltipDisabled", "tooltipTitle"];
import { useCallback, useEffect, useRef } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { TrackedLoadingButton } from 'growth-payments-core/usage-tracker/components/Button';
import { events } from 'growth-payments-core/usage-tracker/events';
import { track } from 'growth-payments-core/usage-tracker/track';
import { ProcessorType } from 'growth-payments-core/eligibility/types';
import { getCommerceHomeZeroStateUrl } from 'growth-payments-core/urls/urls';
import { Metrics } from 'payments-enrollment-embed-lib/utils/metrics';
import { usePaymentsEnrollmentUtilities } from '../../hooks/usePaymentsEnrollmentUtilities';
import useCanOpenPaymentsEnrollment from '../../hooks/useCanOpenPaymentsEnrollment';
import { PaymentsEnrollmentUtilitiesProvider } from '../../context/PaymentsEnrollmentUtilitiesContext';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import PaymentsEnrollmentEmbedBase from '../PaymentsEnrollmentEmbedBase';
import { EnrollmentFlowType } from '../../types';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const mapEnrollmentFlowTypeToProcessor = {
  [EnrollmentFlowType.stripe]: ProcessorType.BYO_STRIPE,
  [EnrollmentFlowType.native]: ProcessorType.HS_PAYMENTS
};
const PaymentsEnrollmentPanelButton = _ref => {
  let {
      enrollmentType = EnrollmentFlowType.processorPicker,
      sourceApp,
      location,
      children,
      viewTrackingAction,
      clickTrackingAction,
      shouldReloadOnEnrollmentComplete = true,
      openInCommerceHome,
      onClick = () => null,
      onPaymentsEnrollmentSuccess = () => null,
      isInProcessorPickerIframe,
      tooltipMaxWidth,
      tooltipPlacement,
      tooltipDisabled,
      tooltipTitle
    } = _ref,
    buttonPassThroughProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const isMountedRef = useRef(true);
  const {
    loading: loadingConnectedAccounts,
    canOpenPaymentsEnrollment
  } = useCanOpenPaymentsEnrollment();
  const paymentsEnrollmentUtilities = usePaymentsEnrollmentUtilities(enrollmentType, sourceApp, location, onPaymentsEnrollmentSuccess);
  const {
    openEnrollmentFlow,
    enrollmentFlowLocation
  } = paymentsEnrollmentUtilities;
  const isDisabled = buttonPassThroughProps.disabled || !canOpenPaymentsEnrollment;
  const getTrackingData = useCallback(() => ({
    page: sourceApp,
    location,
    value: isDisabled ? 'disabled' : 'enabled'
  }), [sourceApp, location, isDisabled]);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  useEffect(() => {
    if (buttonPassThroughProps.loading || loadingConnectedAccounts) return;
    track(events.paymentSettingsInteraction, Object.assign({
      action: viewTrackingAction
    }, getTrackingData()));
  }, [getTrackingData, viewTrackingAction, buttonPassThroughProps.loading, loadingConnectedAccounts]);
  const shouldTrackMetrics = enrollmentType !== EnrollmentFlowType.processorPicker && enrollmentType !== EnrollmentFlowType.select;
  const handleClick = useCallback(() => {
    // Early exit if the component is unmounted
    if (!isMountedRef.current) return;
    if (shouldTrackMetrics) {
      if (canOpenPaymentsEnrollment) {
        Metrics.counter('setup-payments-start', {
          page: sourceApp,
          processor: mapEnrollmentFlowTypeToProcessor[enrollmentType]
        }).increment();
      } else {
        // TODO: Add Raven dep and move this to report to Raven instead of metrics-js
        Metrics.counter('ineligible-setup-payments-start', {
          page: sourceApp,
          processor: mapEnrollmentFlowTypeToProcessor[enrollmentType]
        }).increment();
      }
    }
    if (!openInCommerceHome) {
      onClick();
      if (!isInProcessorPickerIframe) {
        openEnrollmentFlow();
      }
    }
  }, [openEnrollmentFlow, sourceApp, enrollmentType, canOpenPaymentsEnrollment, shouldTrackMetrics, openInCommerceHome, onClick, isInProcessorPickerIframe]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Tooltip
    /**
     * If passed in tooltipDisabled in set and not disabled,
     * let the tooltip-related props take precedence.
     */, Object.assign({}, tooltipDisabled !== undefined && !tooltipDisabled ? {
      maxInlineSize: tooltipMaxWidth,
      placement: tooltipPlacement,
      title: tooltipTitle,
      disabled: tooltipDisabled
    } : {
      disabled: canOpenPaymentsEnrollment,
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "paymentsEnrollmentButton.disabledTooltip.alreadyConnected"
      })
    }, {
      children: /*#__PURE__*/_jsx(TrackedLoadingButton, Object.assign({
        event: events.paymentSettingsInteraction,
        trackingProperties: Object.assign({
          action: clickTrackingAction
        }, getTrackingData()),
        onClick: handleClick,
        use: "primary",
        failed: true,
        href: openInCommerceHome ? `${getCommerceHomeZeroStateUrl()}?paymentsEnrollment=${enrollmentType}` : undefined
      }, buttonPassThroughProps, {
        "data-test-id": buttonPassThroughProps['data-test-id'] || `payments-enrollment-panel-button-${location}`,
        loading: loadingConnectedAccounts || !!buttonPassThroughProps.loading,
        disabled: isDisabled,
        children: children || /*#__PURE__*/_jsx(FormattedMessage, {
          message: "paymentsEnrollmentButton.defaultCopy"
        })
      }))
    })), !isInProcessorPickerIframe && /*#__PURE__*/_jsx(PaymentsEnrollmentUtilitiesProvider, {
      sourceApp: sourceApp,
      location: location,
      enrollmentType: enrollmentType,
      onPaymentsEnrollmentSuccess: onPaymentsEnrollmentSuccess,
      children: /*#__PURE__*/_jsx(PaymentsEnrollmentEmbedBase, {
        shouldReloadOnEnrollmentComplete: shouldReloadOnEnrollmentComplete,
        enrollmentFlowLocation: enrollmentFlowLocation
      })
    })]
  });
};
export default PaymentsEnrollmentPanelButton;